// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Dispositifs sociaux — éléments communs : accès aux listes (organisations, dispositifs, campagnes,
// types d'aide), aux gabarits, au règlement applicable et à la simulation budgétaire ; saisie TYPÉE d'une valeur
// selon le gabarit (montant, durée, date, liste fermée, texte, oui / non, tableau d'éligibilité, barème) ; résultat
// lisible d'une simulation budgétaire. Aucune valeur métier ici : tout vient du gabarit et du registre.
// Routes : backend/src/dispositifs/configuration/README.md § 3 et § 4 ; socle (organisations, dispositifs),
// campagnes (liste), aides (types). Le serveur est seul juge des droits (mandat) : ses refus sont affichés tels quels.
// ═══════════════════════════════════════════════════════════════════════
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { Bouton, BandeauInfo, Badge } from '../../design/composants';
import { Champ } from '../temps/reglagesCommun';

const DS = '/api/dispositifs';
const lireDs = async (url, params) => (await axios.get(`${DS}${url}`, { params })).data.data;
const ecrireDs = async (url, corps) => (await axios.post(`${DS}${url}`, corps || {})).data.data;

/** Tableau défensif (react-component-rules, règle 2). */
export const toArray = (d) => (Array.isArray(d) ? d : (d?.elements || d?.rows || d?.data || []));

export const apiDs = {
  organisations: () => lireDs('/socle/organisations', { taille: 200 }),
  dispositifs: (orgId) => lireDs(`/socle/organisations/${orgId}/dispositifs`, { taille: 200 }),
  dispositif: (id) => lireDs(`/socle/dispositifs/${id}`),
  campagnes: (orgId) => lireDs('/campagnes', { organisation: orgId, taille: 200 }),
  typesAide: (dispositifId) => lireDs(`/aides/dispositifs/${dispositifId}/types`),
  gabarits: () => lireDs('/configuration/gabarits'),
  reglement: (dispositifId, { date, objetType, objetId }) => lireDs(`/configuration/reglements/${dispositifId}`,
    { date: date || undefined, objet_type: objetType || undefined, objet_id: objetId || undefined }),
  simulerEnVigueur: (dispositifId, { date, objetType, objetId, hypotheses }) => ecrireDs(`/configuration/reglements/${dispositifId}/simuler`,
    { date: date || undefined, objet_type: objetType || undefined, objet_id: objetId || undefined, hypotheses: hypotheses || null }),
  packDemo: (dispositifId, { objetType, objetId, dateDebut }) => ecrireDs(`/configuration/reglements/${dispositifId}/pack-demo`,
    { objet_type: objetType || undefined, objet_id: objetId || undefined, date_debut: dateDebut || undefined }),
};

/** Sources d'une règle de dispositif : décisions de l'organisation (DT-CONFIG-003). */
export const SOURCES_DISPOSITIF = {
  STATUTS_ORGANISATION: 'Statuts de l\'organisation', DECISION_AG: 'Décision d\'assemblée générale', DECISION_BUREAU: 'Décision du bureau',
};
export const LIBELLES_GABARIT = {
  CAMPAGNE_VENTE_SUBVENTIONNEE: 'Campagne de vente subventionnée', PRODUIT_PRET_SOCIAL: 'Produit de prêt social', TYPE_AIDE: 'Aides sociales',
};
const UNITES = {
  FCFA: 'F CFA', UNITES: 'unité(s)', MENSUALITES: 'mensualité(s)', PRETS: 'prêt(s)', RANG: 'rang', MOIS: 'mois', POURCENTAGE: '%', JOURS: 'jours',
};
export const uniteDe = (r) => UNITES[r?.unite] || '';
export const aujourdHui = () => new Date().toISOString().slice(0, 10);

/** Erreur du serveur : message + précisions (liste `erreurs`, raisons d'un pack non activable). */
export function precisionsErreur(e) {
  const d = e?.response?.data || {};
  const liste = [...toArray(d.erreurs), ...toArray(d.details?.raisons)];
  return liste.map((x) => (typeof x === 'string' ? x : [x.regle, x.message].filter(Boolean).join(' : '))).filter(Boolean);
}
export function ErreurCfg({ erreur }) {
  if (!erreur) return null;
  return (
    <BandeauInfo ton="danger" titre={erreur.titre}>
      {erreur.message}
      {erreur.precisions?.length > 0 && <ul style={{ margin: '6px 0 0 18px' }}>{erreur.precisions.map((p) => <li key={p}>{p}</li>)}</ul>}
    </BandeauInfo>
  );
}
const TITRES_ERREUR = {
  RETROACTIVITE_INTERDITE: 'Aucun effet rétroactif', AUTO_VALIDATION_INTERDITE: 'Double regard obligatoire', INCOHERENCE: 'Règlement incohérent',
  CHEVAUCHEMENT: 'Chevauchement de règlements', MANDAT_REQUIS: 'Mandat requis', HORS_PERIMETRE: 'Hors de votre périmètre', DEMO_INTERDITE: 'Démonstration refusée',
  SIMULATION_PERIMEE: 'Simulation à refaire', PORTEE_INVALIDE: 'Portée invalide', PACK_NON_ACTIVABLE: 'Règlement non soumis',
};
export const erreurCfg = (e, defaut = 'Opération impossible. Réessayez.') => {
  const d = e?.response?.data || {};
  return { code: d.code || null, titre: TITRES_ERREUR[d.code], message: d.message || defaut, precisions: precisionsErreur(e) };
};

/** Libellé lisible de la portée choisie. */
export function libellePortee({ dispositif, objetType, objet }) {
  if (!dispositif) return '';
  if (!objetType) return `${dispositif.libelle} — dispositif entier${dispositif.type === 'CAMPAGNE' ? ' (valeur commune à toutes ses campagnes)' : dispositif.type === 'AIDE' ? ' (tous les types d\'aide)' : ''}`;
  const nom = objet ? (objet.libelle || objet.numero || objet.code) : 'objet introuvable';
  return `${dispositif.libelle} — ${objetType === 'CAMPAGNE' ? 'campagne' : 'type d\'aide'} « ${nom} »`;
}

/** La règle se règle-t-elle pour cette portée ? (une règle « dispositif » ne se règle pas pour une campagne ou un type). */
export const reglablePour = (r, objetType) => (objetType ? r.portee === objetType : true);

// ─── Valeur typée selon le gabarit ─────────────────────────────────────────────────────────────
/** Valeur initiale d'un champ (valeur proposée par une décision, sinon vide). */
export function valeurInitiale(r, existante) {
  if (existante !== undefined && existante !== null) {
    if (r.type === 'BAREME') return existante.map((p) => ({ seuil: String(p.seuil), valeur: String(p.valeur) }));
    if (r.type === 'MATRICE') return existante;
    if (r.type === 'BOOLEEN') return String(existante);
    return String(existante);
  }
  const proposee = r.valeur_proposee ? r.valeur_proposee.valeur : undefined;
  if (r.type === 'BAREME') return [{ seuil: '', valeur: '' }];
  if (r.type === 'MATRICE') return Object.fromEntries(r.matrice.lignes.map((l) => [l.code, Object.fromEntries(r.matrice.colonnes.map((c) => [c.code, null]))]));
  return proposee === undefined || proposee === null ? '' : String(proposee);
}
/** La valeur est-elle renseignée ? */
export function valeurRemplie(r, v) {
  if (r.type === 'BAREME') return Array.isArray(v) && v.length > 0 && v.every((p) => p.seuil !== '' && p.valeur !== '');
  if (r.type === 'MATRICE') return !!v && Object.values(v).some((l) => Object.values(l).some((x) => x !== null));
  return v !== '' && v !== null && v !== undefined;
}
/** Valeur envoyée au serveur (forme du registre : nombre, chaîne, booléen, paliers, tableau). */
export function valeurEnvoyee(r, v) {
  if (r.type === 'NOMBRE') return Number(String(v).replace(/\s/g, '').replace(',', '.'));
  if (r.type === 'BOOLEEN') return v === 'true';
  if (r.type === 'BAREME') return v.map((p) => ({ seuil: Number(p.seuil), valeur: Number(p.valeur) }));
  if (r.type === 'TEXTE') return String(v).trim();
  return v;
}

function SaisieMatrice({ r, valeur, onChange, id }) {
  const uneColonne = r.matrice.colonnes.length === 1;
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }} aria-labelledby={`${id}-l`}>
      <div className="rh-conteneur-tableau"><table className="rh-tableau" style={{ fontSize: 13 }}>
        <thead><tr><th>Condition</th>{r.matrice.colonnes.map((c) => <th key={c.code}>{uneColonne ? 'Exigée ?' : c.libelle}</th>)}</tr></thead>
        <tbody>{r.matrice.lignes.map((l) => (
          <tr key={l.code}>
            <td>{l.libelle}</td>
            {r.matrice.colonnes.map((c) => {
              const x = valeur?.[l.code]?.[c.code];
              return (
                <td key={c.code}>
                  <select className="rh-champ-saisie" aria-label={`${l.libelle} — ${c.libelle}`} data-ligne={l.code} value={x === null || x === undefined ? '' : String(x)}
                    onChange={(e) => onChange({ ...valeur, [l.code]: { ...valeur[l.code], [c.code]: e.target.value === '' ? null : e.target.value === 'true' } })}>
                    <option value="">À confirmer</option><option value="true">Oui</option><option value="false">Non</option>
                  </select>
                </td>
              );
            })}
          </tr>
        ))}</tbody>
      </table></div>
      <span className="rh-form-aide" style={{ display: 'block' }}>Une case « à confirmer » s'enregistre, mais le règlement n'est pas activable tant qu'elle n'est pas tranchée.</span>
    </fieldset>
  );
}

function SaisieBareme({ r, valeur, onChange }) {
  const maj = (i, k, x) => onChange(valeur.map((p, j) => (j === i ? { ...p, [k]: x } : p)));
  return (
    <div>
      {valeur.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <input type="number" min="0" className="rh-champ-saisie" style={{ maxWidth: 170 }} placeholder={r.bareme.seuil.libelle} aria-label={`${r.bareme.seuil.libelle}, palier ${i + 1}`}
            value={p.seuil} onChange={(e) => maj(i, 'seuil', e.target.value)} />
          <input type="number" min="0" className="rh-champ-saisie" style={{ maxWidth: 170 }} placeholder={r.bareme.valeur.libelle} aria-label={`${r.bareme.valeur.libelle}, palier ${i + 1}`}
            value={p.valeur} onChange={(e) => maj(i, 'valeur', e.target.value)} />
          {valeur.length > 1 && <Bouton petit variante="discret" onClick={() => onChange(valeur.filter((_, j) => j !== i))}>Retirer</Bouton>}
        </div>
      ))}
      <Bouton petit onClick={() => onChange([...valeur, { seuil: '', valeur: '' }])}>Ajouter un palier</Bouton>
    </div>
  );
}

/** Champ de saisie typé d'une règle du gabarit. `id` sert aux tests et à l'accessibilité. */
export function ChampValeurRegle({ r, valeur, onChange, id }) {
  const unite = uniteDe(r);
  if (r.type === 'MATRICE') return <SaisieMatrice r={r} valeur={valeur} onChange={onChange} id={id} />;
  if (r.type === 'BAREME') return <SaisieBareme r={r} valeur={valeur} onChange={onChange} />;
  if (r.type === 'LISTE') {
    return (
      <select id={id} className="rh-champ-saisie" value={valeur} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choisir…</option>
        {toArray(r.valeurs).map((c) => <option key={c} value={c}>{(r.libelles_valeurs && r.libelles_valeurs[c]) || c}</option>)}
      </select>
    );
  }
  if (r.type === 'BOOLEEN') {
    return (
      <select id={id} className="rh-champ-saisie" value={valeur} onChange={(e) => onChange(e.target.value)} style={{ maxWidth: 200 }}>
        <option value="">Choisir…</option><option value="true">Oui</option><option value="false">Non</option>
      </select>
    );
  }
  if (r.type === 'DATE') {
    if (r.premier_du_mois) {
      return <input id={id} type="month" className="rh-champ-saisie" style={{ maxWidth: 200 }} value={valeur ? String(valeur).slice(0, 7) : ''} onChange={(e) => onChange(e.target.value ? `${e.target.value}-01` : '')} />;
    }
    return <input id={id} type="date" className="rh-champ-saisie" style={{ maxWidth: 200 }} value={valeur} onChange={(e) => onChange(e.target.value)} />;
  }
  if (r.type === 'TEXTE') {
    const max = r.longueur_max || 1500;
    return (
      <span style={{ display: 'block' }}>
        {max > 200
          ? <textarea id={id} className="rh-champ-saisie" rows={3} maxLength={max} value={valeur} onChange={(e) => onChange(e.target.value)} />
          : <input id={id} className="rh-champ-saisie" maxLength={max} value={valeur} onChange={(e) => onChange(e.target.value)} />}
        <span className="rh-form-aide" style={{ display: 'block' }}>{String(valeur || '').length} / {max} caractères</span>
      </span>
    );
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input id={id} type="number" inputMode="decimal" min={r.min ?? undefined} max={r.max ?? undefined} step={r.entier ? 1 : 'any'} className="rh-champ-saisie"
        value={valeur} onChange={(e) => onChange(e.target.value)} style={{ maxWidth: 200 }} />
      {unite && <span>{unite}</span>}
    </span>
  );
}

/** Aide d'une règle : bornes de saisie, valeur proposée, défaut décidé. */
export function aideRegle(r) {
  const morceaux = [];
  if (r.aide) morceaux.push(r.aide);
  if (r.type === 'NOMBRE' && r.min !== null && r.max !== null) morceaux.push(`Saisie de ${r.min} à ${r.max}${uniteDe(r) ? ` ${uniteDe(r)}` : ''}.`);
  if (r.premier_du_mois) morceaux.push('Le 1er du mois choisi.');
  if (r.valeur_proposee) morceaux.push(`Valeur proposée (${r.valeur_proposee.reference}) : à vérifier, jamais appliquée sans validation.`);
  if (r.defaut_decide) morceaux.push(`Sans valeur validée : ${r.defaut_decide.libelle}.`);
  return morceaux.join(' ');
}

/** Champ complet (libellé, saisie, aide) — compose un formulaire de règle. Les tableaux sont hors <label>. */
export function ChampRegle({ r, valeur, onChange, id }) {
  if (r.type === 'MATRICE' || r.type === 'BAREME') {
    return (
      <div style={{ marginBottom: 12 }} data-regle={r.code}>
        <span id={`${id}-l`} style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>
          {r.libelle}{r.obligatoire && <span className="rh-form-requis" aria-hidden="true">*</span>}
        </span>
        <ChampValeurRegle r={r} valeur={valeur} onChange={onChange} id={id} />
        {aideRegle(r) && <span className="rh-form-aide" style={{ display: 'block' }}>{aideRegle(r)}</span>}
      </div>
    );
  }
  return (
    <div data-regle={r.code}>
      <Champ libelle={r.libelle} requis={r.obligatoire} aide={aideRegle(r) || undefined}>
        <ChampValeurRegle r={r} valeur={valeur} onChange={onChange} id={id} />
      </Champ>
    </div>
  );
}

// ─── Simulation budgétaire ─────────────────────────────────────────────────────────────────────
const LIBELLES_BUDGET = {
  engagement_achat_stock: 'Engagement d\'achat du stock', inscrits: 'Inscrits (hypothèse)', unites_demandees: 'Unités demandées', cout_total: 'Coût total',
  subvention_totale: 'Subvention totale', creances_totales: 'Créances totales (reste à charge)', retenue_mensuelle_totale_max: 'Retenue mensuelle totale (maximum)',
  reste_a_charge_moyen: 'Reste à charge moyen par inscrit', encours_total: 'Encours total', mensualite: 'Mensualité', derniere: 'Dernière mensualité',
  engagement: 'Engagement', budget_annuel: 'Budget annuel', solde: 'Solde du budget',
};
const SANS_FRANCS = ['inscrits', 'unites_demandees'];
const ESPACE = ' ';
export const francs = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const v = Math.trunc(Number(n));
  return `${v < 0 ? '−' : ''}${String(Math.abs(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ESPACE)}${ESPACE}F`;
};
const nombre = (n) => String(n ?? '—').replace(/\B(?=(\d{3})+(?!\d))/g, ESPACE);

/** Hypothèses de simulation par type de dispositif (README configuration § 4). */
export const HYPOTHESES = {
  PRET: [['demandes', 'Nombre de prêts'], ['montant', 'Montant de chaque prêt (F)'], ['duree', 'Durée (mois)']],
  AIDE: [['demandes', 'Nombre d\'aides'], ['montant_moyen', 'Montant moyen (F)']],
};
const entierSaisi = (x) => /^\d+$/.test(String(x ?? '').replace(/\s/g, ''));
const entierDe = (x) => Number(String(x).replace(/\s/g, ''));

/** Hypothèses saisies → objet attendu par le serveur (null : hypothèse par défaut du serveur). */
export function hypothesesDe(type, h) {
  if (type === 'CAMPAGNE') {
    const profils = toArray(h.profils).filter((p) => entierSaisi(p.quantite) && entierSaisi(p.inscrits));
    return profils.length ? { repartition: profils.map((p) => ({ quantite: entierDe(p.quantite), inscrits: entierDe(p.inscrits) })) } : null;
  }
  const champs = HYPOTHESES[type] || [];
  const o = Object.fromEntries(champs.filter(([k]) => entierSaisi(h[k])).map(([k]) => [k, entierDe(h[k])]));
  return Object.keys(o).length ? o : null;
}

/** Formulaire d'hypothèses (campagne : profils quantité × inscrits ; prêt ; aide). Valeurs vides : hypothèse par défaut. */
export function FormulaireHypotheses({ type, valeur, onChange }) {
  if (type === 'CAMPAGNE') {
    const profils = toArray(valeur.profils).length ? valeur.profils : [{ quantite: '', inscrits: '' }];
    const maj = (i, k, x) => onChange({ ...valeur, profils: profils.map((p, j) => (j === i ? { ...p, [k]: x } : p)) });
    return (
      <div>
        <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>Profils d'inscription hypothétiques (quantité demandée × nombre d'inscrits). Laissez vide : hypothèse haute du serveur (tous les salariés actifs, une unité).</p>
        {profils.map((p, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, alignItems: 'end' }}>
            <Champ libelle={`Quantité par inscrit${profils.length > 1 ? ` (profil ${i + 1})` : ''}`}>
              <input className="rh-champ-saisie" inputMode="numeric" data-hyp="quantite" value={p.quantite} onChange={(e) => maj(i, 'quantite', e.target.value)} />
            </Champ>
            <Champ libelle="Nombre d'inscrits">
              <input className="rh-champ-saisie" inputMode="numeric" data-hyp="inscrits" value={p.inscrits} onChange={(e) => maj(i, 'inscrits', e.target.value)} />
            </Champ>
          </div>
        ))}
        {profils.length < 20 && <Bouton petit variante="discret" onClick={() => onChange({ ...valeur, profils: [...profils, { quantite: '', inscrits: '' }] })}>Ajouter un profil</Bouton>}
      </div>
    );
  }
  const champs = HYPOTHESES[type] || [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
      {champs.map(([k, l]) => (
        <Champ key={k} libelle={l}><input className="rh-champ-saisie" inputMode="numeric" data-hyp={k} value={valeur[k] || ''} onChange={(e) => onChange({ ...valeur, [k]: e.target.value })} /></Champ>
      ))}
    </div>
  );
}

function Tuile({ libelle, valeur }) {
  return (
    <div className="rh-carte" style={{ padding: '8px 10px', margin: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--sg-texte-2)' }}>{libelle}</div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{valeur}</div>
    </div>
  );
}

/**
 * Résultat lisible d'une simulation budgétaire (règlement en vigueur, pack, ou pack soumis — `budget_dispositif`).
 * Aucune donnée n'est modifiée par une simulation.
 */
export function ResultatBudget({ resultat }) {
  if (!resultat) return null;
  const s = resultat.budget_dispositif || resultat;
  const budget = s.budget || {};
  const tuiles = Object.entries(budget).filter(([, v]) => v !== null && v !== undefined);
  const anomalies = toArray(s.anomalies);
  return (
    <div data-resultat-simulation="">
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
        Simulation au {s.date_reference || '—'}{resultat.cree_le ? ' · conservée' : ''} · aucune donnée modifiée{resultat.a_jour === false ? ' · ' : ''}
        {resultat.a_jour === false && <Badge ton="alerte">Périmée : relancez-la</Badge>}
      </p>
      {s.calcul_impossible && <BandeauInfo ton="alerte" titre="Calcul impossible">{s.calcul_impossible}</BandeauInfo>}
      {tuiles.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 10 }}>
          {tuiles.map(([k, v]) => <Tuile key={k} libelle={LIBELLES_BUDGET[k] || k} valeur={SANS_FRANCS.includes(k) ? nombre(v) : francs(v)} />)}
        </div>
      )}
      {s.hypotheses?.origine && <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>{s.hypotheses.origine}</p>}
      {toArray(s.par_profil).length > 0 && (
        <div className="rh-conteneur-tableau" style={{ marginBottom: 10 }}>
          <table className="rh-tableau" style={{ fontSize: 13 }}>
            <thead><tr><th>Quantité</th><th>Inscrits</th><th>Prix total</th><th>Aide</th><th>Reste à charge</th><th>Mensualité</th><th>Dernière</th><th>Mensualités</th></tr></thead>
            <tbody>{s.par_profil.map((p) => (
              <tr key={`${p.quantite}-${p.inscrits}`}>
                <td>{p.quantite}</td><td>{nombre(p.inscrits)}</td><td>{francs(p.total)}</td><td>{francs(p.aide)}</td><td>{francs(p.reste)}</td>
                <td>{francs(p.mensualite)}</td><td>{francs(p.derniere)}</td><td>{p.nb_mensualites}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {anomalies.map((a) => (
        <BandeauInfo key={`${a.code}-${a.message}`} ton={a.gravite === 'BLOQUANT' ? 'danger' : 'alerte'} titre={a.gravite === 'BLOQUANT' ? 'Anomalie bloquante' : 'Avertissement'}>{a.message}</BandeauInfo>
      ))}
      {toArray(s.regles_absentes).length > 0 && (
        <p style={{ margin: '0 0 8px', fontSize: 13, display: 'flex', gap: 6 }}>
          <AlertTriangle size={14} aria-hidden="true" /> Règles obligatoires encore absentes : {s.regles_absentes.map((x) => x.libelle).join(' ; ')}.
        </p>
      )}
      {s.quotite?.message && <p className="rh-form-aide" style={{ margin: '0 0 8px' }}>{s.quotite.message}</p>}
      {toArray(s.regles).length > 0 && (
        <details style={{ fontSize: 13 }}>
          <summary>Valeurs utilisées ({s.regles.length})</summary>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {s.regles.map((x) => <li key={x.code}>{x.libelle} : <strong>{x.valeur}</strong>{x.origine === 'SIMULEE' ? ' (en préparation)' : x.origine === 'EN_VIGUEUR' ? ' (en vigueur)' : ''}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Impact simulé d'une version de règle (avant → après, budget recalculé). */
export function ImpactVersion({ simulation }) {
  if (!simulation) return <BandeauInfo ton="alerte" titre="Pas encore simulée">L'effet de cette version sur le règlement et le budget n'a pas encore été calculé.</BandeauInfo>;
  return (
    <div className="rh-carte" style={{ padding: 12, marginBottom: 12 }}>
      <p style={{ margin: '0 0 6px', fontSize: 14 }}>
        Au {simulation.date_reference || '—'} : <strong>{simulation.avant ?? '—'}</strong> → <strong>{simulation.valeur_simulee ?? '—'}</strong> · aucune donnée modifiée
      </p>
      <ResultatBudget resultat={{ ...simulation, regles: [], date_reference: simulation.date_reference }} />
    </div>
  );
}
